import { Paper } from '@mui/material'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Color } from '../../../../chess/types'
import { ENV } from '../../../../env'
import { analyticsManager } from '../../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import ReconnectingWebSocket, {
    ErrorEvent,
    Options,
    WS_ERROR_EVENT_STATUS,
} from '../../../../socket/ReconnectingWebsocket'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import store from '../../../../store/store'
import { ConnectionStatus, GameData, GameResult, GameResultType } from '../../../../store/types'
import { MM_MESSAGES } from '../../../matchMakingDrawer/matchMakingConnector/MMTypes'
import { GameViewState, StateChangeRequest } from '../../GameViewModel'
import { getGameResultType } from '../gameOverDialog/utils'
import { GS_MESSAGES, GS_REQUESTS } from './GSTypes'

const END_OF_GAME_DELAY = 500 // ms

const GameServerConnector: React.FC = () => {
    const serverConnectionData = useStoreState((state) => state.serverConnectionData)
    const authToken = useStoreState((state) => state.token)
    const userID = useStoreState((state) => state.userData.userData?.id)
    const userData = useStoreState((state) => state.userData.userData)
    const matchData = useStoreState((state) => state.gameView.matchData)
    const connectionStatus = useStoreState((state) => state.gameView.connectionStatus)
    const gameStartedTimestamp = useStoreState((state) => state.gameView.gameStartedTimestamp)
    const gameState = useStoreState((state) => state.gameView.gameState)
    const needsResync = useStoreState((state) => state.gameView.needsResync)
    const queueTimeStarts = useStoreState((state) => state.queueTimeStarts)

    const setSocket = useStoreActions((state) => state.gameView.setSocket)
    const receiveMove = useStoreActions((state) => state.gameView.receiveMove)
    const receiveClockUpdate = useStoreActions((state) => state.gameView.receiveClockUpdate)
    const beforeFirstMove = useStoreActions((state) => state.gameView.beforeFirstMove)
    const initialiseGame = useStoreActions((state) => state.gameView.initialiseGame)
    const sendJoin = useStoreActions((state) => state.gameView.sendJoin)
    const setGameResult = useStoreActions((state) => state.gameView.setGameResult)
    const sendGameResultToAnalytics = useStoreActions((state) => state.gameView.sendGameResultToAnalytics)
    const addMoveOpening = useStoreActions((state) => state.gameView.addMoveOpening)
    const setDrawerOpen = useStoreActions((state) => state.setDrawerOpen)
    const setIsDrawerMenuOpen = useStoreActions((state) => state.setIsDrawerMenuOpen)
    const setDrawStatus = useStoreActions((state) => state.gameView.setDrawStatus)
    const setConnectionStatus = useStoreActions((state) => state.gameView.setConnectionStatus)
    const setGameState = useStoreActions((state) => state.gameView.setGameState)
    const resetGame = useStoreActions((state) => state.gameView.resetGame)
    const setChallenges = useStoreActions((state) => state.matchMaker.setChallenges)
    const getScore = useStoreActions((state) => state.matchMaker.getScore)
    const setGameEndOpen = useStoreActions((state) => state.gameView.setGameEndEnabled)
    const shutdownGSConnection = useStoreActions((state) => state.gameView.shutDownGSConnection)
    const receiveMoveRequestAcknowledgement = useStoreActions(
        (state) => state.gameView.receiveMoveRequestAcknowledgement,
    )
    const setOpponentDisconnected = useStoreActions((state) => state.gameView.setOpponentDisconnected)
    const setOpponentConnected = useStoreActions((state) => state.gameView.setOpponentConnected)
    const setCanClaimVictory = useStoreActions((state) => state.gameView.setCanClaimVictory)
    const setMatchData = useStoreActions((state) => state.gameView.setMatchData)
    const initializeGameOnReconnect = useStoreActions((state) => state.gameView.initializeGameOnReconnect)
    const updateDeferredGameTree = useStoreActions((state) => state.gameView.updateDeferredGameTree)
    const setProcessingMove = useStoreActions((state) => state.gameView.setProcessingMove)
    const clearQueueTimeStarts = useStoreActions((state) => state.clearQueueTimeStarts)
    const clearPostGameSocket = useStoreActions((state) => state.gameView.clearPostGameSocket)
    const keepSocketForEmotes = useStoreActions((state) => state.emotes.keepSocketForEmotes)
    const receiveEmoteReply = useStoreActions((state) => state.emotes.receiveReply)
    const receiveEmote = useStoreActions((state) => state.emotes.receivePush)
    const navigate = useNavigate()

    useEffect(
        () => {
            console.log(
                `rendering gameServerConnector. conectionStatus: ${connectionStatus} matchData: ${JSON.stringify(
                    matchData,
                )}`,
            )
            if (
                authToken &&
                connectionStatus === ConnectionStatus.NOT_INITIALIZED &&
                matchData &&
                !!matchData.gameServiceUrl
            ) {
                // A URL-provider function, not a frozen string — so a reconnect the
                // class triggers internally (dropped connection, appResume) re-reads
                // the live token instead of replaying the one this socket was built
                // with. gameServiceUrl itself is fine closed-over: it's fixed for the
                // life of this match.
                const url = () => `${matchData.gameServiceUrl}/api/ws?token=${store.getState().token}`
                const options: Options = {
                    debug: true,
                    debugName: 'GS',
                    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, then 30s max
                    minReconnectionDelay: 1000,
                    maxReconnectionDelay: 30000,
                    reconnectionDelayGrowFactor: 2.0,
                    // Only 40 retries (down from 600)
                    maxRetries: 40,
                    // Keepalive: every 4s, 1s timeout (original setting)
                    // TODO: Consider increasing timeout for mobile/Safari after coordinating with backend team
                    keepaliveDelay: 4000,
                    keepaliveTimeout: 1000,
                    // Jitter prevents thundering herd
                    useJitter: true,
                }
                let ws = new ReconnectingWebSocket(url, undefined, options)

                // Track if this was an intentional close (game ended)
                let intentionalClose = false
                // Set when the game ended but the server keeps this socket for emotes only (postGame.emotes)
                let postGame = false

                // Store original close method to intercept intentional closes
                const originalClose = ws.close.bind(ws)
                ws.close = (...args) => {
                    intentionalClose = true
                    return originalClose(...args)
                }

                ws.addEventListener('close', () => {
                    if (postGame) {
                        // the post-game socket never reconnects and must not touch the game's connection status
                        clearPostGameSocket(ws)
                        return
                    }
                    console.log('game server ws closed, intentional:', intentionalClose)
                    // Only set to CONNECTING if this wasn't an intentional disconnect
                    if (!intentionalClose) {
                        console.log('game server ws closed unexpectedly, attempting to reconnect')
                        setConnectionStatus(ConnectionStatus.CONNECTING)
                    } else {
                        console.log('game server ws closed intentionally (game ended), not reconnecting')
                    }
                })

                ws.addEventListener('open', () => {
                    if (postGame) return
                    setConnectionStatus(ConnectionStatus.CONNECTED)
                    setSocket(ws)
                    if (matchData.messageType === MM_MESSAGES.PLAYER_IS_PLAYING) {
                        sendJoin()
                    }
                    console.log(`gameserver ws opened, sending join request`)
                })

                // NOTE: Doesn't triggers...
                ws.addEventListener('reconnect', () => {
                    if (postGame) return
                    console.log('gameserver ws reconencted after it broke')
                    setConnectionStatus(ConnectionStatus.CONNECTED)
                    setSocket(ws)
                    sendJoin()
                })

                ws.addEventListener('error', (ev: ErrorEvent) => {
                    console.log(`GameServer error: ${ev.message}`)
                    // TIMEOUT / empty message are transient states the reconnect
                    // logic below already handles — record as a breadcrumb, not an
                    // error. Only the terminal MAX_RETRIES is worth reporting.
                    Sentry.addBreadcrumb({
                        category: 'ws',
                        level: 'info',
                        message: `gameserver error: ${ev.message || 'no message'}`,
                    })
                    if (postGame) return
                    switch (ev.message) {
                        case WS_ERROR_EVENT_STATUS.TIMEOUT:
                            setConnectionStatus(ConnectionStatus.CONNECTING)
                            break
                        case WS_ERROR_EVENT_STATUS.MAX_RETRIES:
                            Sentry.captureMessage('GameServer connection failed after max retries', 'warning')
                            setConnectionStatus(ConnectionStatus.RECONNECT_FAILED)
                            break
                        default:
                            break
                    }
                })

                ws.addEventListener('message', (e) => {
                    let mData
                    try {
                        mData = JSON.parse(e.data)
                    } catch {
                        Sentry.addBreadcrumb({
                            category: 'ws',
                            level: 'warning',
                            message: 'gameserver: non-JSON frame',
                        })
                        return
                    }
                    if (mData.requestType === GS_REQUESTS.SEND_EMOTE) {
                        receiveEmoteReply(mData)
                        return
                    }
                    if (mData.messageType === GS_MESSAGES.EMOTE) {
                        receiveEmote(mData)
                        return
                    }
                    // after the game the socket only carries emotes
                    if (postGame) return

                    if (mData.messageType === GS_MESSAGES.MOVE_AND_CLOCK && mData.data) {
                        // ----------------------------------------------------------- move update
                        if (mData.data.lastMove && mData.result?.status !== 'error') {
                            const update = {
                                moveStr: mData.data.lastMove,
                                clock: mData.data.clock,
                                opening: mData.data.opening,
                                receivedAt: Date.now(),
                            }
                            receiveMove(update)
                        } else if (mData.data.lastMove === '') {
                            beforeFirstMove()
                        }
                    } else if (
                        (mData.messageType === GS_MESSAGES.CLOCK_UPDATED ||
                            mData.messageType === GS_MESSAGES.MATCH_STARTED) &&
                        mData.data
                    ) {
                        // ----------------------------------------------------------- clock starts or update
                        if (mData.data.whiteLeft) {
                            const update = {
                                clockBlack: mData.data.blackLeft,
                                clockWhite: mData.data.whiteLeft,
                            }
                            receiveClockUpdate(update)
                        }

                        if (gameState !== GameViewState.GAME_RUNNING) {
                            setGameState(GameViewState.GAME_RUNNING)
                        }
                    } else if (mData.messageType === GS_MESSAGES.END_OF_GAME && mData.data && mData.data.method) {
                        // ----------------------------------------------------------- end of game
                        if (mData.data.postGame?.emotes && keepSocketForEmotes(ws)) {
                            postGame = true
                        } else {
                            shutdownGSConnection()
                        }
                        setOpponentDisconnected(undefined)
                        setDrawerOpen('closed')
                        setTimeout(() => {
                            setGameEndOpen(true)
                        }, END_OF_GAME_DELAY)
                        setCanClaimVictory(false)

                        const d = mData.data
                        const result: GameResult = {
                            winner: undefined,
                            type: GameResultType.Unknown,
                            newRating: undefined,
                            clock: d.clock,
                            outcome: d.outcome,
                            botdRewardURL: d.botdRewardURL,
                        }

                        if (d.ratings) result.newRating = d.ratings

                        // check the result who won ("0-1" is a fixed notation in chess!)
                        if (d.outcome === '1-0') result.winner = Color.White
                        else if (d.outcome === '0-1') result.winner = Color.Black

                        // decipher the reason
                        result.type = getGameResultType(d.reason)

                        setGameResult(result)
                        sendGameResultToAnalytics({
                            clock: d.clock,
                            reason: d.reason,
                            method: d.method,
                            newRating: d.ratings,
                        })
                        setMatchData({
                            matchId: '',
                            gameServiceUrl: '',
                            messageType: MM_MESSAGES.CHALLENGE_COMPLETED,
                            result: undefined,
                        })
                    } else if (mData.messageType === GS_MESSAGES.OPPONENT_DISCONNECT) {
                        // ----------------------------------------------------------- opponent disconnected
                        if (mData.data.secondsTillVictoryClaim)
                            setOpponentDisconnected({
                                timestamp: Date.now(),
                                secondsTillVictoryClaim: mData.data.secondsTillVictoryClaim,
                            })
                    } else if (mData.messageType === GS_MESSAGES.OPPONENT_CONNECTED) {
                        setOpponentConnected(true)
                        // ----------------------------------------------------------- opponent reconnected
                        setOpponentDisconnected(undefined)
                        setCanClaimVictory(false)
                        setDrawerOpen('closed')
                    } // ----------------------------------------------------------- can claim victory
                    else if (mData.messageType === GS_MESSAGES.MATCH_IS_VALID_FOR_CLAIM) {
                        setCanClaimVictory(true)
                    } else if (mData.requestType === GS_REQUESTS.MATCH_CLAIM_VICTORY) {
                        setCanClaimVictory(false)
                    } else if (mData.requestType === GS_REQUESTS.MATCH_WAIT_FOR_OPPONENT) {
                        setCanClaimVictory(false)
                    } // ----------------------------------------------------------- join response
                    else if (mData.requestType === GS_REQUESTS.JOIN && userID) {
                        if (mData.data && mData.data.matchKind) {
                            // technically 1077 means the match is not found but so far this is the consequence of a timeout
                            if (mData.result.errorCode === 1076 || mData.result.errorCode === 1077) {
                                setGameResult({
                                    type: GameResultType.Timeout,
                                    winner: undefined,
                                })

                                shutdownGSConnection()
                            }

                            const gd: GameData = mData.data

                            navigate('/')
                            setGameEndOpen(false)

                            if (
                                mData.data.state === 'InProgress' ||
                                mData.data.state === 'DrawIsOffered' ||
                                mData.data.state === 'AbortIsOffered'
                            ) {
                                initializeGameOnReconnect({
                                    gameData: mData.data,
                                    timeStamp: mData.timeStamp,
                                    userID: userID!,
                                })
                            } else {
                                initialiseGame({ gameData: gd, userID, timeStamp: mData.timeStamp })
                            }
                            setChallenges([] as any)

                            if (
                                mData.data.matchKind === 'human_vs_human' &&
                                matchData.messageType !== MM_MESSAGES.CHALLENGE_ACCEPTED
                            ) {
                                getScore({
                                    challengerId: mData.data.player1.playerId,
                                    challengeeId: mData.data.player2.playerId,
                                })
                            }

                            const timeInQueue = queueTimeStarts.find((q) => q.pairId === mData.data.matchId)
                            const timeInQueueMs = Date.now() - (timeInQueue ? timeInQueue.timestamp : 0)

                            // GTM expects separate timeInQueue event
                            if (timeInQueue) {
                                analyticsManager.dispatchEvent('timeInQueue', {
                                    timeInQueue: timeInQueueMs / 1000, // Convert to seconds
                                    rated: gd.rated,
                                    challengeString: `${gd.timeMode.durationMinutes}+${gd.timeMode.clockIncrementSeconds}`,
                                    challengeType: 'auto_pool',
                                    matchedFromQueue: true,
                                })
                            }

                            analyticsManager.dispatchEvent('gameStarted', {
                                matchKind: gd.matchKind,
                                minutes: gd.timeMode.durationMinutes,
                                increment: gd.timeMode.clockIncrementSeconds,
                                rated: gd.rated,
                                timeInQueue: timeInQueueMs,
                                player1ID: gd.player1.playerId,
                                player2ID: gd.player2.playerId,
                                player1Color: gd.player1.playerColor,
                                player2Color: gd.player2.playerColor,
                                matchID: gd.matchId,
                                // GTM required fields
                                challengeString: `${gd.timeMode.durationMinutes}+${gd.timeMode.clockIncrementSeconds}`,
                                matchSource: timeInQueue ? 'auto_pool' : 'direct_challenge',
                                difficulty: gd.matchKind === 'human_vs_ai' ? gd.difficulty?.level : undefined,
                            })
                            clearQueueTimeStarts()

                            if (mData.data.state === 'DrawIsOffered') {
                                setDrawStatus(StateChangeRequest.RECEIVED)
                            }
                        } else {
                            if (mData.result.errorMessage === 'match not found') {
                                // Expected: stale/expired match id. Handled gracefully
                                // below (reopen drawer, reset) — not an error.
                                Sentry.addBreadcrumb({
                                    category: 'gs',
                                    level: 'info',
                                    message: 'GS - Match not found (stale match, resetting)',
                                })
                                setDrawerOpen('open')
                                resetGame(GameViewState.PRE_GAME)
                                ws.close()
                            }
                        }

                        // ----------------------------------------------------------- move response (opening?)
                    } else if (mData.requestType === GS_REQUESTS.MOVE && mData.data) {
                        if (mData.data.opening && mData.correlationId) {
                            addMoveOpening({ moveID: mData.correlationId, opening: mData.data.opening })
                        }

                        if (mData.result?.status === 'success') {
                            receiveMoveRequestAcknowledgement({ clock: mData.data.clock })
                            // DEV-2412
                            // setTimeout(updateDeferredGameTree, 200)
                            updateDeferredGameTree()
                        } else {
                            setProcessingMove(false)
                        }
                        // ----------------------------------------------------------- draw offer received
                    } else if (mData.messageType === GS_MESSAGES.DRAW_OFFER) {
                        setDrawStatus(StateChangeRequest.RECEIVED)
                        // ----------------------------------------------------------- draw offer response
                    } else if (mData.requestType === GS_REQUESTS.DRAW_OFFER && mData.result) {
                        if (mData.result.status === 'success') {
                            setDrawStatus(StateChangeRequest.REQUESTED)
                        }
                        // ----------------------------------------------------------- abort offer response
                    } else if (mData.messageType === GS_MESSAGES.MATCH_ABORTED && mData.result) {
                        if (mData.result.status === 'success') {
                            //
                        }
                        // ----------------------------------------------------------- draw offer rejected
                    } else if (mData.requestType === GS_REQUESTS.DRAW_REJECTED && mData.result) {
                        if (mData.result.status === 'success') {
                            //
                        }
                        // ----------------------------------------------------------- draw cancelled
                        else if (mData.messageType === GS_MESSAGES.DRAW_OFFER_REMOVED) {
                            setDrawStatus(StateChangeRequest.REMOVED)
                        }
                        // ----------------------------------------------------------- abort cancelled
                    } else if (mData.messageType === GS_MESSAGES.CONNECT_ON_ANOTHER_DEVICE) {
                        shutdownGSConnection()
                        resetGame(GameViewState.PRE_GAME)
                        setOpponentDisconnected(undefined)
                        if (window.location.pathname === '/') {
                            setDrawerOpen('open')
                            setIsDrawerMenuOpen(true)
                        }
                    }
                })
            }
        },
        // TODO: evaluate if this is correct
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            addMoveOpening,
            authToken,
            connectionStatus,
            initialiseGame,
            matchData,
            receiveMove,
            sendJoin,
            setConnectionStatus,
            setDrawStatus,
            setDrawerOpen,
            setGameResult,
            setSocket,
            userID,
            receiveClockUpdate,
            userData?.user_name,
            gameStartedTimestamp,
            updateDeferredGameTree,
            setCanClaimVictory,
        ],
    )

    // receiveMove could not place a move: the join reply (initializeGameOnReconnect) rebuilds the tree from the PGN
    useEffect(() => {
        if (needsResync) sendJoin()
    }, [needsResync, sendJoin])

    return (
        <Paper
            className={'serverConnection gs'}
            style={{
                display: serverConnectionData ? 'block' : 'none',
            }}
        >
            GS: {connectionStatus} {matchData?.gameServiceUrl || ''} {ENV}{' '}
        </Paper>
    )
}

export default GameServerConnector
